import { prefs, type Lang } from "./prefs";

/**
 * Number, date and time formatting: the only place the app turns numbers and dates
 * into text (ALP-84, glossary "Numbers and time").
 *
 * - Arabic uses **Western digits (0-9)** and the Gregorian calendar (owner decision Q7).
 *   Never call `toLocaleString("ar")` or `Intl.*Format("ar-EG")` directly: those emit
 *   Arabic-Indic digits (٠-٩). Use these helpers, or `locale(lang)`.
 * - **One time format**: 12-hour clock in both languages ("3:45 pm" / "3:45 م"),
 *   for events, messages and chart axes alike (`fmtTime`, `fmtHour`).
 * - Time zones are shown as a city and an offset, never the IANA id (`fmtZone`).
 * - Inside Arabic text, wrap numbers with units, handles and URLs in <Ltr/> or <Num/>.
 */
export const locale = (lang: Lang = prefs.get().lang) => (lang === "ar" ? "ar-u-nu-latn-ca-gregory" : "en-GB");

const cache = new Map<string, Intl.NumberFormat>();
function nf(opts: Intl.NumberFormatOptions, lang?: Lang) {
  const key = `${locale(lang)}|${JSON.stringify(opts)}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale(lang), opts);
    cache.set(key, f);
  }
  return f;
}

export const fmtNumber = (n: number | null | undefined, lang?: Lang) => (n === null || n === undefined ? "—" : nf({}, lang).format(n));
export const fmtCompact = (n: number | null | undefined, lang?: Lang) =>
  n === null || n === undefined ? "—" : nf({ notation: "compact", maximumFractionDigits: 1 }, lang).format(n);
export const fmtPercent = (n: number | null | undefined, digits = 0, lang?: Lang) =>
  n === null || n === undefined || !Number.isFinite(n) ? "—" : nf({ style: "percent", maximumFractionDigits: digits }, lang).format(n);
export const fmtSignedPercent = (n: number, digits = 0, lang?: Lang) =>
  nf({ style: "percent", maximumFractionDigits: digits, signDisplay: "exceptZero" }, lang).format(n);

/** Change between two values as a fraction (0.12 = +12%); null when undefined. */
export function delta(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (current === null || current === undefined || previous === null || previous === undefined || previous === 0) return null;
  return (current - previous) / Math.abs(previous);
}

/** Money: Western digits in both languages ("$1,234.50" / "US$ 1,234.50"). */
export const fmtMoney = (n: number | null | undefined, lang?: Lang, digits = 2, currency = "USD") =>
  n === null || n === undefined || !Number.isFinite(n) ? "—" : nf({ style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits }, lang).format(n);

/**
 * A 0..1 model score (confidence, toxicity) for customers: a plain level, never "0.93".
 * Show it as t(`common.level.<level>`); staff screens may add `fmtPercent(n)`.
 */
export function scoreLevel(n: number | null | undefined): "low" | "medium" | "high" | "unknown" {
  if (n === null || n === undefined || !Number.isFinite(n)) return "unknown";
  if (n >= 0.75) return "high";
  if (n >= 0.4) return "medium";
  return "low";
}

/** The one 12-hour clock: hour + minutes with am/pm ("3:45 pm" / "3:45 م"). */
const TIME: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit", hour12: true };

export function fmtDate(d: string | Date, opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" }, lang?: Lang) {
  // any option set that shows the hour follows the one time format
  const o = opts.hour !== undefined && opts.hour12 === undefined && opts.hourCycle === undefined ? { ...opts, hour: "numeric" as const, hour12: true } : opts;
  return new Intl.DateTimeFormat(locale(lang), o).format(typeof d === "string" ? new Date(d) : d);
}
export const fmtDateTime = (d: string | Date, lang?: Lang) => fmtDate(d, { day: "numeric", month: "short", ...TIME }, lang);
export const fmtTime = (d: string | Date, lang?: Lang) => fmtDate(d, TIME, lang);
/** An hour of the day (0-23) for chart axes and heatmaps: "3 pm" / "3 م". */
export const fmtHour = (h: number, lang?: Lang) => fmtDate(new Date(2020, 0, 1, h), { hour: "numeric", hour12: true }, lang);
/** A wall-clock "HH:mm" string (quiet hours, schedules) in the one time format. */
export function fmtClock(hhmm: string, lang?: Lang) {
  const [h, m] = hhmm.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  return fmtDate(new Date(2020, 0, 1, h!, m!), TIME, lang);
}
export const fmtWeekday = (d: string | Date, style: "short" | "long" = "short", lang?: Lang) => fmtDate(d, { weekday: style }, lang);

export function fmtRelative(d: string | Date, lang: Lang = prefs.get().lang, now = Date.now()) {
  const t = typeof d === "string" ? new Date(d).getTime() : d.getTime();
  const s = Math.round((t - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale(lang), { numeric: "auto", style: "short" });
  const abs = Math.abs(s);
  if (abs < 45) return rtf.format(0, "second");
  if (abs < 3600) return rtf.format(Math.round(s / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(s / 3600), "hour");
  if (abs < 86400 * 7) return rtf.format(Math.round(s / 86400), "day");
  if (abs < 86400 * 30) return rtf.format(Math.round(s / (86400 * 7)), "week");
  return fmtDate(new Date(t), { day: "numeric", month: "short" }, lang);
}

/** "3h 12m" style countdown (used by the 24h DM window). */
export function fmtDuration(ms: number, lang: Lang = prefs.get().lang) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const u = lang === "ar" ? { h: "س", m: "د", s: "ث" } : { h: "h", m: "m", s: "s" };
  if (h > 0) return `${h}${u.h} ${String(m).padStart(2, "0")}${u.m}`;
  if (m > 0) return `${m}${u.m} ${String(s).padStart(2, "0")}${u.s}`;
  return `${s}${u.s}`;
}

export function fmtMinutes(min: number, lang: Lang = prefs.get().lang) {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (lang === "ar") return h ? `${h} س ${m} د` : `${m} د`;
  return h ? `${h}h ${m}m` : `${m}m`;
}

/** Arabic names for the cities behind the IANA zones our customers use most. */
const AR_CITIES: Record<string, string> = {
  Amman: "عمّان", Riyadh: "الرياض", Dubai: "دبي", Kuwait: "الكويت", Qatar: "قطر", Bahrain: "البحرين", Muscat: "مسقط",
  Baghdad: "بغداد", Damascus: "دمشق", Beirut: "بيروت", Jerusalem: "القدس", Gaza: "غزة", Hebron: "الخليل", Cairo: "القاهرة",
  Tripoli: "طرابلس", Tunis: "تونس", Algiers: "الجزائر", Casablanca: "الدار البيضاء", Khartoum: "الخرطوم", Aden: "عدن",
  Istanbul: "إسطنبول", Tehran: "طهران", Karachi: "كراتشي", Kolkata: "كولكاتا", London: "لندن", Paris: "باريس", Berlin: "برلين",
  Madrid: "مدريد", Rome: "روما", Amsterdam: "أمستردام", Stockholm: "ستوكهولم", Moscow: "موسكو", New_York: "نيويورك",
  Chicago: "شيكاغو", Denver: "دنفر", Los_Angeles: "لوس أنجلوس", Toronto: "تورنتو", Sao_Paulo: "ساو باولو", Tokyo: "طوكيو",
  Shanghai: "شنغهاي", Singapore: "سنغافورة", Sydney: "سيدني", Kuala_Lumpur: "كوالالمبور", Jakarta: "جاكرتا", UTC: "التوقيت العالمي",
};

/**
 * A time zone as a person reads it: city + offset, "Amman (GMT+3)" / "عمّان (GMT+3)".
 * Customers never see the IANA id ("Asia/Amman").
 */
export function fmtZone(tz: string | null | undefined, lang: Lang = prefs.get().lang, at: Date = new Date()) {
  if (!tz) return "—";
  let offset: string;
  try {
    offset = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" }).formatToParts(at).find((p) => p.type === "timeZoneName")?.value ?? "GMT";
  } catch {
    return tz;
  }
  const last = tz === "UTC" || tz === "Etc/UTC" ? "UTC" : (tz.split("/").pop() ?? tz);
  const city = lang === "ar" ? (AR_CITIES[last] ?? last.replace(/_/g, " ")) : last === "UTC" ? "UTC" : last.replace(/_/g, " ");
  return `${city} (${offset === "GMT" ? "GMT+0" : offset})`;
}

/** Eastern Arabic (٠-٩) and Persian (۰-۹) digits → Western, for text we did not format ourselves. */
export const westernDigits = (s: string) =>
  s.replace(/[\u0660-\u0669]/g, (c) => String(c.charCodeAt(0) - 0x0660)).replace(/[\u06F0-\u06F9]/g, (c) => String(c.charCodeAt(0) - 0x06f0)).replace(/\u066A/g, "%");

/** A language code from the AI labels ("ar", "en") as a name in the UI language: "Arabic" / "العربية". */
export function fmtLanguage(code: string | null | undefined, lang: Lang = prefs.get().lang) {
  if (!code) return "—";
  try {
    return new Intl.DisplayNames([lang === "ar" ? "ar" : "en"], { type: "language" }).of(code) ?? code;
  } catch {
    return code;
  }
}
